import React, { useEffect, useRef, useState } from 'react';
import { FuelButton } from '@frete.com/fuel-react';
import { ToastJourneyInterface } from '@components/ui/JourneyList/JourneyListType';
import useAmplitude from '@hooks/useAmplitude';
import { useTimeout } from '@hooks/useTimeout';
import { proposalNotificationRead } from '@services/proposals';
import { ProposalConfirmedSuccessEventInterface } from '../../../config/dom';
import { NotificationPropsInterface } from './NotificationTypes';
import * as Styled from './NotificationStyles';

function Notification({ onClickAction }: NotificationPropsInterface) {
  const { amplitudeEvent } = useAmplitude();
  const addTimeout = useTimeout();
  const toastRefConfirmation = useRef<HTMLFuelToastElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const [toastData, setToastData] = useState({} as ToastJourneyInterface);

  function displayToast() {
    return new Promise<void>((resolve) => {
      addTimeout(() => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        resolve();
      }, 300);
    });
  }

  function handleShowToast(data: ToastJourneyInterface) {
    displayToast().then(() => {
      resetToast();
      addTimeout(() => {
        setToastData(data);
      }, 0);
    });
  }

  function handleNotificationShow(
    dataEvent: CustomEvent<ToastJourneyInterface>,
  ) {
    handleShowToast(dataEvent.detail);
  }

  function handleNotificationProposalConfirmedSuccess(
    dataEvent: CustomEvent<ProposalConfirmedSuccessEventInterface>,
  ) {
    const data = dataEvent.detail;

    handleShowToast({
      journeyId: data.journeyId,
      journeyCode: data.journeyCode,
      message: data.message,
      severity: data.isReject ? 'neutral' : 'success',
      action: data.isReject
        ? undefined
        : {
            slug: 'monitoringJourney',
            text: 'Monitorar viagem',
          },
    });
  }

  function handleNotificationProposalConfirmedError(
    dataEvent: CustomEvent<{
      message: {
        title: string;
        description: string;
      };
    }>,
  ) {
    const data = dataEvent.detail;

    handleShowToast({
      heading: data.message.title,
      message: data.message.description,
      severity: 'error',
    });
  }

  function handleClickAction() {
    if (toastRefConfirmation.current) {
      toastRefConfirmation.current.close();
    }

    if (toastData.action) {
      onClickAction(toastData.action, toastData.journeyId);
    }
  }

  function resetToast() {
    setToastData({
      heading: '',
      message: '',
      notificationId: '',
      severity: 'neutral',
      journeyId: '',
      flow: undefined,
    });
  }

  function handleToast() {
    if ((toastData.heading || toastData.message) && toastData.severity) {
      return (
        <Styled.FloatingToast
          ref={toastRefConfirmation}
          heading={toastData.heading}
          message={toastData.message}
          severity={toastData.severity}
          onFuel-toast-close={() => {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
            }
            resetToast();
          }}
        >
          {toastData.action && (
            <FuelButton slot="button" onClick={() => handleClickAction()}>
              {toastData.action.text}
            </FuelButton>
          )}
        </Styled.FloatingToast>
      );
    }

    return null;
  }

  function handleSendMetrics() {
    if ((toastData.heading || toastData.message) && toastData.severity) {
      if (toastData.flow === 'finished') {
        amplitudeEvent('journey_finish_toast_show', {
          source:
            toastData.severity === 'success'
              ? 'sucesso-confirmacao-entrega'
              : 'erro-confimacao-entrega',
          journey_id: toastData.journeyId || '',
          message: toastData.message || toastData.heading || '',
        });
      }

      if (toastData.flow === 'updateAddress') {
        amplitudeEvent('journey_address_update_toast_show', {
          source:
            toastData.severity === 'success'
              ? 'sucesso-adicionar-rota'
              : 'erro-adicionar-rota',
          message: toastData.message || toastData.heading || '',
          status_code: toastData.statusCode || '',
          journey_id: toastData.journeyId || '',
        });
      }

      if (toastData.flow === 'truckAbandonmentNotice') {
        amplitudeEvent('truck_warning_toast_show', {
          source: 'aviso-de-desistencia-do-caminhoneiro',
          message: toastData.message || toastData.heading || '',
          status_code: toastData.statusCode || '',
          notificationId: toastData.notificationId || '',
        });
      }
    }
  }

  function handleProposalNotificationRead() {
    if ((toastData.heading || toastData.message) && toastData.severity) {
      if (
        toastData.flow === 'truckAbandonmentNotice' &&
        toastData.notificationId
      ) {
        proposalNotificationRead(toastData.notificationId)
          .then((response) => {
            amplitudeEvent('truck_warning_notification_read', {
              source: 'sucesso-notificacao-lida',
              message: toastData.message || toastData.heading || '',
              status_code: response.status || '',
              notificationId: toastData.notificationId || '',
            });
          })
          .catch((error) => {
            amplitudeEvent('truck_warning_notification_read', {
              source: 'erro-notificacao-lida',
              message: toastData.message || toastData.heading || '',
              status_code: error.status || '',
              notificationId: toastData.notificationId || '',
            });
          });
      }
    }
  }

  handleSendMetrics();
  handleProposalNotificationRead();

  useEffect(() => {
    if (
      toastRefConfirmation.current &&
      (toastData.heading || toastData.message) &&
      toastData.severity
    ) {
      if (toastRefConfirmation.current.open) {
        toastRefConfirmation.current.open();
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = addTimeout(() => {
        if (toastRefConfirmation?.current) {
          toastRefConfirmation.current.close();
        }
      }, 8000);
    }
  }, [toastData]);

  useEffect(() => {
    window.addEventListener('notificationShow', handleNotificationShow);
    window.addEventListener(
      'proposalConfirmedSuccess',
      handleNotificationProposalConfirmedSuccess,
    );
    window.addEventListener(
      'proposalConfirmedError',
      handleNotificationProposalConfirmedError,
    );

    return () => {
      window.removeEventListener('notificationShow', handleNotificationShow);
      window.removeEventListener(
        'proposalConfirmedSuccess',
        handleNotificationProposalConfirmedSuccess,
      );
      window.removeEventListener(
        'proposalConfirmedError',
        handleNotificationProposalConfirmedError,
      );
    };
  }, []);

  return <>{handleToast()}</>;
}

export default Notification;
